import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "rc-slider/assets/index.css";

import { LuSave } from "react-icons/lu";
import { GiCrossedBones } from "react-icons/gi";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import InsurerDropdown from "../../../../utils/Insurer";
import { useProductOperations, useGetProductByIdQuery } from "../../../../services/redux/api/components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
 
/* Compact multi-select (same UX as PolicyRegister.MultiSelectInline) */
function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select..." }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200  h-10 w-full flex items-center gap-2 justify-between bg-white`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap overflow-hidden">
          {value.length === 0 ? (
            <span className="text-gray-400  truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap overflow-hidden">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span key={id} className="text-xs bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap">
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{value.length > 0 ? `${value.length} selected` : ""}</span>
          <FaChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md max-h-52 overflow-auto p-2">
          {options.map((o) => (
            <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
              <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
              <span className=" text-gray-700">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
let Range = null;
let Slider = null;
try {
  const rc = require("rc-slider");
  if (rc) {
    // support Range and single-handle Slider
    if (rc.Range) {
      Range = rc.Range;
    }
    if (rc.Slider) {
      Slider = rc.Slider;
    }
    if (!Range && rc.default) {
      Range = rc.default.Range || rc.default;
    }
    if (!Slider && rc.default) {
      Slider = rc.default.Slider || rc.default;
    }
  }
} catch (err) {
  Range = null;
}

/* ---------------------------
   constants & validation
----------------------------*/
// generate options for Term/PPT (1..99)
const termOptions = Array.from({ length: 99 }, (_, i) => String(i + 1));
const pptOptions = Array.from({ length: 99 }, (_, i) => String(i + 1));
const paymentModeOptions = ["Annual", "Half-Yearly", "Quarterly", "Monthly"];

const validationSchema = Yup.object({
  insurer: Yup.string().required("Insurer is required"),
  productName: Yup.string().required("Product Name is required"),
  productCode: Yup.string().required("Product Code is required"),
  minSum: Yup.number().required("Min Sum is required"),
  maxSum: Yup.number().required("Max Sum is required"),
  minAge: Yup.number().required("Min Age is required"),
  maxAge: Yup.number().required("Max Age is required"),
  paymentMode: Yup.mixed(),
  productStartDate: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("Product Start Date is required"),
  productCloseDate: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("Product Close Date is required")
    .when('productStartDate', (productStartDate, schema) =>
      productStartDate ? schema.min(productStartDate, 'Product Close Date cannot be before Product Start Date') : schema
    ),
});

const SUM_MIN = 200000; // 2,00,000
const SUM_MAX = 20000000; // 2,00,00,000
const AGE_MIN = 18;
const AGE_MAX = 99;

const currencyFormatter = new Intl.NumberFormat("en-IN");

/* ---------------------------
   Component
----------------------------*/
export default function AddLifeProductMaster({ initialData = null } = {}) {
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const routeId = params?.id ?? null;

  // fetch product when route provides an id (edit mode)
  const { data: fetchedResp, isLoading: isFetching } = useGetProductByIdQuery(routeId, { skip: !routeId });
  const fetchedProduct = fetchedResp?.data ?? null;

  // editingProduct is either the fetched product (when editing via route) or initialData prop
  const editingProduct = fetchedProduct || initialData || null;

  // canonical product id (support different field names returned by API)
  const editingProductId = editingProduct?.ProductID ?? editingProduct?.ProductId ?? editingProduct?.id ?? null;

  // initial values derive from editingProduct when available
  const initialValues = {
    insurer: editingProduct?.InsurerID ?? editingProduct?.Insurer ?? "",
    productName: editingProduct?.ProductName ?? "",
    productShortName: editingProduct?.ProductShortName ?? "",
    productCode: editingProduct?.ProductCode ?? "",
    // term and ppt as arrays for multiselect UI; backend will receive CSV
    term: editingProduct && editingProduct.Term ? String(editingProduct.Term).split(',').map((s) => s.trim()) : [],
    ppt: editingProduct && editingProduct.PPT ? String(editingProduct.PPT).split(',').map((s) => s.trim()) : [],
    minSum: editingProduct?.MinSum ?? SUM_MIN,
    maxSum: editingProduct?.MaxSum ?? SUM_MAX,
    minAge: editingProduct?.MinAge ?? AGE_MIN,
    maxAge: editingProduct?.MaxAge ?? AGE_MAX,
    // paymentMode supports multiple selections; store as array
    paymentMode: editingProduct && editingProduct.PaymentModes ? String(editingProduct.PaymentModes).split(',').map((s) => s.trim()) : [],
    productStartDate: editingProduct?.ProductStartDate ? editingProduct.ProductStartDate.split('T')[0] : "",
    productCloseDate: editingProduct?.ProductCloseDate ? editingProduct.ProductCloseDate.split('T')[0] : "",
  };

  const { saveProduct, updateProduct, deleteProduct, isProcessing } = useProductOperations();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    // Validate product dates
    if (values.productStartDate && values.productCloseDate) {
      const s = new Date(values.productStartDate);
      const c = new Date(values.productCloseDate);
      if (c < s) {
        toast.error('Product Close Date cannot be before Product Start Date', { position: 'top-right', autoClose: 4000 });
        setSubmitting(false);
        return;
      }
    }

    const payload = {
    Mode: editingProductId ? 'Update' : 'Create',
    ProductID: editingProductId ?? null,
      InsurerID: values.insurer ? (Number.isNaN(Number(values.insurer)) ? values.insurer : parseInt(values.insurer, 10)) : null,
      ProductName: values.productName,
      ProductShortName: values.productShortName || "",
      ProductCode: values.productCode,
      // Term/PPT/PaymentModes support multiselect in UI; send as comma separated strings
      Term: Array.isArray(values.term) ? values.term.join(',') : (values.term ? String(values.term) : null),
      PPT: Array.isArray(values.ppt) ? values.ppt.join(',') : (values.ppt ? String(values.ppt) : null),
      PaymentModes: Array.isArray(values.paymentMode) ? values.paymentMode.join(',') : (values.paymentMode || ""),
      MinSum: values.minSum ? Number(values.minSum) : null,
      MaxSum: values.maxSum ? Number(values.maxSum) : null,
      MinAge: values.minAge ? Number(values.minAge) : null,
      MaxAge: values.maxAge ? Number(values.maxAge) : null,
      ProductStartDate: values.productStartDate || null,
      ProductCloseDate: values.productCloseDate || null,
      IsActive: true,
    };

    try {
      let res;
      if (payload.Mode === 'Update') {
        setIsUpdating(true);
        // use updateProduct mutation for clarity
        res = await updateProduct(payload).unwrap();
      } else {
        res = await saveProduct(payload).unwrap();
      }

      if (res && (res.success || res.Status === "SUCCESS")) {
        const successMsg = res.message || (payload.Mode === 'Update' ? 'Product updated successfully' : 'Product saved successfully');
        toast.success(successMsg, { position: "top-right", autoClose: 3000 });
        if (payload.Mode === 'Update') {
          navigate('/life-product');
        } else {
          resetForm();
        }
      } else {
        const msg = res?.message || JSON.stringify(res);
        toast.error((payload.Mode === 'Update' ? 'Update' : 'Save') + " failed: " + msg, { position: "top-right", autoClose: 5000 });
      }
    } catch (err) {
      const msg = (err && err.data && (err.data.message || err.data.Message)) || err?.message || "Network/server error";
      console.error("save/updateProduct error:", err);
      toast.error((payload.Mode === 'Update' ? 'Update' : 'Save') + " failed: " + msg, { position: "top-right", autoClose: 5000 });
    } finally {
      setIsUpdating(false);
      setSubmitting(false);
    }
  };

  // Cancel: reset the form and navigate back (or to a specific route)
  const handleCancel = (resetForm) => {
    resetForm(); // clear form fields
    toast.info("Form cleared", { position: "top-right", autoClose: 1500 });
    // navigate back in history; replace with navigate('/products') if you prefer a specific route
    navigate(-1);
  };

  return (
    <div className="formContainer lg:p-4 rounded">
      <ToastContainer />
      <div className="flex flex-col p-4 lg:mx-5 border rounded bg-white">
        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">
          Life Product Master
        </h5>

  <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting, resetForm }) => (
            <Form>
              {/* top: 4 columns on md+, 1 column on small */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-3">
                <div className="md:col-span-1">
                  <div className="w-full">
                    <InsurerDropdown name="insurer" insurerType="L" />
                  </div>
                </div>

                <div className="md:col-span-1 mb-3">
                  <label className="form-label">Product Name</label>
                  <Field name="productName" className="custom-shadow form-control " />
                  <ErrorMessage name="productName" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="md:col-span-1">
                  <label className="form-label">Product Short Name</label>
                  <Field name="productShortName" className="custom-shadow form-control" />
                </div>
                
                <div className="md:col-span-1">
                  <label className="form-label">Product Code</label>
                  <Field name="productCode" className="custom-shadow form-control" />
                  <ErrorMessage name="productCode" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="form-label">Term </label>
                  <MultiSelectInline
                    options={termOptions.map((t) => ({ id: t, label: t }))}
                    value={values.term}
                    onChange={(v) => setFieldValue('term', v)}
                    placeholder="Select term(s)..."
                  />
                </div>

                <div>
                  <label className="form-label">PPT</label>
                  <MultiSelectInline
                    options={pptOptions.map((p) => ({ id: p, label: p }))}
                    value={values.ppt}
                    onChange={(v) => setFieldValue('ppt', v)}
                    placeholder="Select PPT(s)..."
                  />
                </div>

                <div>
                  <label className="form-label">Payment Modes</label>
                  <MultiSelectInline
                    options={paymentModeOptions.map((m) => ({ id: m, label: m }))}
                    value={values.paymentMode}
                    onChange={(v) => setFieldValue('paymentMode', v)}
                    placeholder="Select payment mode(s)..."
                  />
                  <ErrorMessage name="paymentMode" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              {/* advanced toggle */}
              <div className="flex justify-center mb-6">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((s) => !s)}
                  className="flex items-center gap-3 bg-red-600 text-white px-5 py-2 rounded-lg shadow-lg focus:outline-none"
                >
                  {advancedOpen ? <FaChevronUp /> : <FaChevronDown />}
                  <span className="font-semibold">{advancedOpen ? "Hide Advanced" : "Show Advanced"}</span>
                </button>
              </div>

              {/* Advanced pane */}
              {advancedOpen && (
                <div className="bg-gray-50 rounded-lg p-5 mb-6">
                  {/* Min/Max Sum */}
                  <div className="mb-6">
                    

                    <div className="px-2">
                      {Slider ? (
                        <div className="flex gap-4 items-center">
                          {/* Left: Min Sum slider with numeric input at its right */}
                          <div className="flex-1 flex items-center">
                            <div className="flex-1">
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-xs text-gray-500">Min Sum</div>
                                <div className="text-sm font-medium">{currencyFormatter.format(values.minSum)}</div>
                              </div>
                              <Slider
                                min={SUM_MIN}
                                max={SUM_MAX}
                                value={Number(values.minSum)}
                                onChange={(val) => {
                                  const v = Number(val);
                                  const maxAllowed = Number(values.maxSum) || SUM_MAX;
                                  const newVal = Math.min(v, maxAllowed);
                                  setFieldValue("minSum", newVal);
                                }}
                                step={1000}
                                trackStyle={{ backgroundColor: "#0b5d84", height: 8 }}
                                railStyle={{ backgroundColor: "#d7e2e9", height: 8 }}
                                handleStyle={{ borderColor: "#c6e0ef", backgroundColor: "#ffffff", width: 20, height: 20 }}
                              />
                            </div>
                          </div>

                          {/* Right: Max Sum slider with numeric input at its left. Use reverse to anchor visually to right. */}
                          <div className="flex-1 flex items-center">
                            <div className="flex-1">
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-xs text-gray-500">Max Sum</div>
                                <div className="text-sm font-medium">{currencyFormatter.format(values.maxSum)}</div>
                              </div>
                              <Slider
                                min={SUM_MIN}
                                max={SUM_MAX}
                                value={Number(values.maxSum)}
                                onChange={(val) => {
                                  const v = Number(val);
                                  const minAllowed = Number(values.minSum) || SUM_MIN;
                                  const newVal = Math.max(v, minAllowed);
                                  setFieldValue("maxSum", newVal);
                                }}
                                step={1000}
                                reverse={true}
                                trackStyle={{ backgroundColor: "#0b5d84", height: 8 }}
                                railStyle={{ backgroundColor: "#d7e2e9", height: 8 }}
                                handleStyle={{ borderColor: "#c6e0ef", backgroundColor: "#ffffff", width: 20, height: 20 }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-sm text-red-500">Slider not available — edit values below.</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs text-gray-500">Min Sum</label>
                              <div className="flex items-center gap-2">
                                <Field name="minSum" type="number" min={SUM_MIN} max={SUM_MAX} className="form-control custom-shadow" />
                              </div>
                              <ErrorMessage name="minSum" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            <div>
                              <label className="text-xs text-gray-500">Max Sum</label>
                              <div className="flex items-center gap-2">
                                <Field name="maxSum" type="number" min={SUM_MIN} max={SUM_MAX} className="form-control custom-shadow" />
                              </div>
                              <ErrorMessage name="maxSum" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="text-xs text-gray-500">Min Sum (Exact)</label>
                        <div className="flex items-center gap-2">
                          <Field name="minSum" type="number" min={SUM_MIN} max={SUM_MAX} className="form-control custom-shadow" />
                        </div>
                        <ErrorMessage name="minSum" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Max Sum (Exact)</label>
                        <div className="flex items-center gap-2">
                          <Field name="maxSum" type="number" min={SUM_MIN} max={SUM_MAX} className="form-control custom-shadow" />
                        </div>
                        <ErrorMessage name="maxSum" component="div" className="text-red-500 text-sm mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* Min/Max Age */}
                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <div className="text-xs text-gray-500">Min Age</div>
                        <div className="text-sm font-medium">{values.minAge}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Max Age</div>
                        <div className="text-sm font-medium">{values.maxAge}</div>
                      </div>
                    </div>

                    <div className="px-2">
                      {Slider ? (
                        <div className="flex gap-4 items-center">
                          {/* Left: Min Age slider */}
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-2">
                              <div className="text-xs text-gray-500">Min Age</div>
                              <div className="text-sm font-medium">{values.minAge}</div>
                            </div>
                            <Slider
                              min={AGE_MIN}
                              max={AGE_MAX}
                              value={Number(values.minAge)}
                              onChange={(val) => {
                                const v = Number(val);
                                const maxAllowed = Number(values.maxAge) || AGE_MAX;
                                const newVal = Math.min(v, maxAllowed);
                                setFieldValue("minAge", newVal);
                              }}
                              step={1}
                              trackStyle={{ backgroundColor: "#0b5d84", height: 8 }}
                              railStyle={{ backgroundColor: "#d7e2e9", height: 8 }}
                              handleStyle={{ borderColor: "#c6e0ef", backgroundColor: "#ffffff", width: 20, height: 20 }}
                            />
                          </div>

                          {/* Right: Max Age slider */}
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-2">
                              <div className="text-xs text-gray-500">Max Age</div>
                              <div className="text-sm font-medium">{values.maxAge}</div>
                            </div>
                            <Slider
                              min={AGE_MIN}
                              max={AGE_MAX}
                              value={Number(values.maxAge)}
                              onChange={(val) => {
                                const v = Number(val);
                                const minAllowed = Number(values.minAge) || AGE_MIN;
                                const newVal = Math.max(v, minAllowed);
                                setFieldValue("maxAge", newVal);
                              }}
                              step={1}
                              reverse={true}
                              trackStyle={{ backgroundColor: "#0b5d84", height: 8 }}
                              railStyle={{ backgroundColor: "#d7e2e9", height: 8 }}
                              handleStyle={{ borderColor: "#c6e0ef", backgroundColor: "#ffffff", width: 20, height: 20 }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-sm text-red-500">Slider not available — edit age values below.</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs text-gray-500">Min Age</label>
                              <div className="flex items-center gap-2">
                                <Field name="minAge" type="number" min={AGE_MIN} max={AGE_MAX} className="form-control custom-shadow" />
                              </div>
                              <ErrorMessage name="minAge" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            <div>
                              <label className="text-xs text-gray-500">Max Age</label>
                              <div className="flex items-center gap-2">
                                <Field name="maxAge" type="number" min={AGE_MIN} max={AGE_MAX} className="form-control custom-shadow" />
                              </div>
                              <ErrorMessage name="maxAge" component="div" className="text-red-500 text-sm mt-1" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="text-xs text-gray-500">Min Age</label>
                        <div className="flex items-center gap-2">
                          <Field name="minAge" type="number" min={AGE_MIN} max={AGE_MAX} className="form-control custom-shadow" />
                        </div>
                        <ErrorMessage name="minAge" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Max Age</label>
                        <div className="flex items-center gap-2">
                          <Field name="maxAge" type="number" min={AGE_MIN} max={AGE_MAX} className="form-control custom-shadow" />
                        </div>
                        <ErrorMessage name="maxAge" component="div" className="text-red-500 text-sm mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <label className="text-sm text-gray-600 mb-2 block">Product Start Date</label>
                      <Field name="productStartDate">
                        {({ field, form }) => (
                          <input
                            {...field}
                            type="date"
                            className="form-control custom-shadow"
                            onChange={(e) => {
                              const newVal = e.target.value;
                              form.setFieldValue('productStartDate', newVal);
                              // If close date exists and is before new start date, bump it to be equal
                              const closeVal = form.values.productCloseDate;
                              if (closeVal && new Date(closeVal) < new Date(newVal)) {
                                form.setFieldValue('productCloseDate', newVal);
                              }
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="productStartDate" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div className="relative">
                      <label className="text-sm text-gray-600 mb-2 block">Product Close Date</label>
                      <Field name="productCloseDate">
                        {({ field, form }) => (
                          <input
                            {...field}
                            type="date"
                            className="form-control custom-shadow"
                            // disable choosing a date earlier than productStartDate
                            min={form.values.productStartDate || undefined}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="productCloseDate" component="div" className="text-red-500 text-sm mt-1" />
                    </div>
                  </div>
                </div>
              )}

              {/* actions */}
              <div className="flex justify-center gap-4 mt-6">
                <button type="submit" disabled={isSubmitting || isProcessing || isUpdating} className="saveButton flex items-center gap-2">
                  <LuSave /> {isUpdating ? "Updating..." : (isSubmitting || isProcessing ? "Saving..." : "Save")}
                </button>

                <button type="button" onClick={() => handleCancel(resetForm)} className="backButton flex items-center gap-2">
                  <GiCrossedBones /> Cancel
                </button>
                {editingProductId && (
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={async () => {
                      const ok = window.confirm('Are you sure you want to delete this product?');
                      if (!ok) return;
                      setIsDeleting(true);
                      try {
                        await deleteProduct(editingProductId).unwrap();
                        toast.success('Product deleted successfully', { position: 'top-right', autoClose: 2500 });
                        navigate('/life-product');
                      } catch (err) {
                        const msg = (err && err.data && (err.data.message || err.data.Message)) || err?.message || 'Delete failed';
                        console.error('deleteProduct error:', err);
                        toast.error('Delete failed: ' + msg, { position: 'top-right', autoClose: 4000 });
                      } finally {
                        setIsDeleting(false);
                      }
                    }}
                    className="delete-button"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}